import { type StrictlyBasisPoints, basisPointsFromDollars } from "@vyne-shared/utils/currency";
import { balanceAges } from "../../balanceMap";
import type { BulkRequestFilterState } from "./useBulkRequestFilter";

/** Compiled bulk request filter, ready for use as a predicate. */
export type BulkRequestFilter = ReturnType<typeof compileBulkRequestFilter>;

/**
 * Compile bulk request filtering configuration to a filter that can serve as predicate for bulk request items.
 * @param state The bulk request filtering configuration.
 * @returns The compiled bulk request filter.
 */
export function compileBulkRequestFilter(state: BulkRequestFilterState) {
    return {
        /** Balance Aging buckets selected for display, if applicable. */
        agings: state.agings.size !== balanceAges.length ? state.agings : undefined,

        /** Minimum Balance filter, if applicable. */
        minBalance: state.minBalanceEnabled && state.minBalance ? toBasisPoints(state.minBalance) : undefined,

        /** Patient name substring filter, in lower case. Empty string if not applicable. */
        patient: state.patient ? state.patient.toLowerCase() : "",

        includeOutstandingClaims: state.includeOutstandingClaims,
        includeActivePaymentPlans: state.includeActivePaymentPlans,
    } as const;
}

function toBasisPoints(balance: number) {
    const points = validateBalance(balance);
    return points !== 0 ? points : undefined;
}

/**
 * Validate a raw ("as typed by user") balance string to a defined value in basis points.
 * @param balance Balance string as typed by the user. Empty or invalid values acceptable.
 * @returns The formatted balance string, or 0 if input was empty or otherwise not formattable.
 */
export function validateBalance(balance: number) {
    try {
        let points = basisPointsFromDollars(balance);
        if (points < 0) {
            points = 0 as StrictlyBasisPoints;
        } else if (points > 100000000) {
            points = 100000000 as StrictlyBasisPoints;
        }
        return points;
    } catch {
        return 0 as StrictlyBasisPoints;
    }
}
